import { useState, useEffect, useRef } from "react";
import {
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonIcon,
  IonLabel,
  IonInput,
  IonItem,
  IonText,
  IonTitle,
  IonSegment,
  IonSegmentButton,
  IonModal,
  IonHeader,
  IonToolbar,
  IonContent,
  IonButtons,
} from "@ionic/react";
import {
  close,
  closeCircleOutline,
  personOutline,
  person,
  search,
} from "ionicons/icons";
import { useContactSearch, ContactFilter } from "./useContactSearch";
import { displayMsg, Spinner } from "pymble-ui/components";

interface ContactItem {
  _id: string;
  Name?: string;
  Email?: string;
  StaffDetails?: string;
  StudentDetails?: string;
  ContactIdLookup?: string;
  [key: string]: unknown;
}

const memberExists = (id: string, existing: string[]) => {
  return (
    Array.isArray(existing) &&
    existing.find((e: string) => {
      return e === id;
    })
  );
};

export const PopoverMemberList: React.FC<{
  onHide: () => void;
  contacts: ContactItem[];
  existing: string[];
  onSelect: (contact: ContactItem) => void;
  onSearch: (search: Partial<ContactFilter>) => void;
  onLoading: boolean;
  title?: string;
}> = ({
  onHide,
  contacts,
  existing,
  onSelect,
  onSearch,
  onLoading,
  title,
}) => {
  const [searchText, setSearchText] = useState("");
  const [contactType, setContactType] = useState<string>("staff");
  const searchInput = useRef<HTMLIonInputElement>(null);

  useEffect(() => {
    onSearch({ search: searchText, type: [contactType] });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onSearch is not stable
  }, [searchText, contactType]);

  useEffect(() => {
    setTimeout(() => {
      searchInput?.current?.setFocus();
    }, 600);
  }, []);

  return (
    <>
      <IonHeader>
        <IonToolbar color="light">
          <IonTitle>{title ? title : "Add New Members"}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onHide}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonGrid className="member-popover-grid">
          <IonRow>
            <IonCol size="4">
              <IonSegment
                onIonChange={(e) => {
                  setContactType(e.detail.value ? String(e.detail.value) : "staff");
                }}
                value={contactType}
                color="primary"
                className="select-member-type-segment"
              >
                <IonSegmentButton value="staff" className="ion-text-capitalize">
                  <IonLabel>Staff</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="student" className="ion-text-capitalize">
                  <IonLabel>Student</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="carer" className="ion-text-capitalize">
                  <IonLabel>Carer</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCol>
            <IonCol>
              <IonItem>
                <IonIcon icon={search} slot="start" />
                <IonInput
                  ref={searchInput}
                  placeholder="Search"
                  debounce={500}
                  onIonInput={(e) => setSearchText(e.detail.value || "")}
                ></IonInput>
                <IonIcon
                  icon={closeCircleOutline}
                  slot="end"
                  className={searchText ? "" : "ion-hide"}
                  onClick={() => {
                    setSearchText("");
                    if (searchInput.current) searchInput.current.value = "";
                  }}
                />
              </IonItem>
            </IonCol>
          </IonRow>
          {contacts &&
            contacts.map((contact, i: number) => {
              // Does contact existing in group?
              const exists = memberExists(contact._id, existing);

              return (
                <IonRow
                  key={i}
                  onClick={() => onSelect(contact)}
                  className="select-member-row"
                >
                  <IonCol size="1">
                    <IonIcon icon={exists ? person : personOutline} />
                  </IonCol>
                  <IonCol size="3">
                    <IonLabel>{contact.Name}</IonLabel>
                  </IonCol>
                  <IonCol size="5" className="ion-text-justify">
                    <IonText>{contact.Email}</IonText>
                  </IonCol>
                  <IonCol size="3">
                    {contact.StaffDetails && (
                      <IonText>{contact.StaffDetails}</IonText>
                    )}
                    {contact.StudentDetails && (
                      <IonText>{contact.StudentDetails}</IonText>
                    )}
                  </IonCol>
                </IonRow>
              );
            })}
          <IonRow>
            <IonCol className="ion-float-right">
              {onLoading && <Spinner />}
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    </>
  );
};

/**
 * Hook to select group members
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook is co-located with its popover component
export function useSelectMemberPopover(currentMembers: ContactItem[], title?: string) {
  const [existingMemberIds, setExistingMemberIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const {
    contactSearchResult,
    contactSearchResultLoaded,
    setContactSearchFilter,
    contactSearchFilter,
  } = useContactSearch();

  const [selectedMember, setSelectedMember] = useState<ContactItem>();

  const handleSelectContact = (contact: ContactItem) => {
    // Does contact existing in group?
    const exists = memberExists(contact._id, existingMemberIds);
    if (exists) {
      displayMsg(`${contact.Name} has already been added`, {
        type: "warning",
        position: "middle",
        duration: 1000,
      });
    } else {
      if (!contact.ContactIdLookup) {
        // Add our existance check field if it is missing
        contact["ContactIdLookup"] = contact._id;
      }
      setSelectedMember(contact);
    }
  };
  const handleSearch = (filter: Partial<ContactFilter>) => {
    const newSearch = { ...contactSearchFilter, ...filter };
    setContactSearchFilter(newSearch);
  };

  // Hook to set an array of ids of existing group members.
  // We can use this to highlight existing members in search result
  useEffect(() => {
    const members: string[] = currentMembers.map((m) => m.ContactIdLookup ?? m._id);
    setExistingMemberIds(members);
  }, [currentMembers]);

  const presentContactPopover = (_opts?: Record<string, unknown>) => {
    setModalOpen(true);
  };

  const dismissContactPopover = () => {
    setModalOpen(false);
  };

  const MemberModal = (
    <IonModal
      isOpen={modalOpen}
      onDidDismiss={dismissContactPopover}
      className="select-member-popover"
    >
      <PopoverMemberList
        onHide={dismissContactPopover}
        contacts={contactSearchResult as unknown as ContactItem[]}
        existing={existingMemberIds}
        onSelect={handleSelectContact}
        onSearch={handleSearch}
        onLoading={!contactSearchResultLoaded}
        title={title}
      />
    </IonModal>
  );

  return {
    presentContactPopover,
    dismissContactPopover,
    handleSelectContact,
    selectedMember,
    PopoverMemberList,
    MemberModal,
  };
}
